import { useState, useEffect, useRef, useCallback } from "react";
import {
  useBookAutocomplete,
  type BookSuggestion,
} from "@/lib/hooks/useBookAutocomplete";

interface BookAutocompleteProps {
  label?: string;
  error?: string;
  placeholder?: string;
  value?: string;
  onSelect?: (book: BookSuggestion) => void;
  onChange?: (value: string) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
}

const BookAutocomplete = ({
  label,
  error,
  placeholder = "Start typing book title or author...",
  value = "",
  onSelect,
  onChange,
  onPaste,
  name,
  id,
}: BookAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    suggestions,
    isLoading,
    error: searchError,
    search,
    clearSuggestions,
  } = useBookAutocomplete();

  const inputId =
    id || label?.toLowerCase().replace(/\s+/g, "-") || "book-autocomplete";

  // Handle input value changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      setInputValue(newValue);
      setSelectedIndex(-1);

      if (onChange) {
        onChange(newValue);
      }

      if (newValue.length >= 2) {
        search(newValue);
        setIsOpen(true);
      } else {
        clearSuggestions();
        setIsOpen(false);
      }
    },
    [onChange, search, clearSuggestions]
  );

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (book: BookSuggestion) => {
      setInputValue(book.title);
      setIsOpen(false);
      clearSuggestions();
      setSelectedIndex(-1);

      if (onSelect) {
        onSelect(book);
      }
      if (onChange) {
        onChange(book.title);
      }
    },
    [onSelect, onChange, clearSuggestions]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[selectedIndex]);
          }
          break;
        case "Escape":
          setIsOpen(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, suggestions, selectedIndex, handleSuggestionSelect]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Show dropdown when there are suggestions
  useEffect(() => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  }, [suggestions]);

  const baseInputClassName = `w-full p-4 border rounded-xl outline-none transition-colors ${
    error
      ? "border-error-500 focus:border-error-500"
      : "border-neutral-400 focus:border-neutral-600"
  }`;

  return (
    <div className="w-full flex flex-col gap-1 relative" ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="text-[18px] font-bold">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          className={baseInputClassName}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          autoComplete="off"
          onPaste={onPaste}
        />

        {/* Loading spinner */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-400 border-t-transparent"></div>
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && (suggestions.length > 0 || isLoading || searchError) && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-neutral-300 rounded-xl shadow-lg max-h-60 overflow-y-auto mt-1">
          {isLoading && (
            <div className="px-4 py-3 text-neutral-500 text-sm">
              Searching...
            </div>
          )}

          {searchError && !isLoading && (
            <div className="px-4 py-3 text-error-500 text-sm">
              {searchError}
            </div>
          )}

          {!isLoading &&
            !searchError &&
            suggestions.length === 0 &&
            inputValue.length >= 2 && (
              <div className="px-4 py-3 text-neutral-500 text-sm">
                No processed books found
              </div>
            )}

          {suggestions.map((book, index) => (
            <div
              key={book.id}
              className={`px-4 py-3 cursor-pointer transition-colors border-b border-neutral-200 last:border-b-0 ${
                index === selectedIndex
                  ? "bg-book-100 text-book-700"
                  : "hover:bg-neutral-100"
              }`}
              onClick={() => handleSuggestionSelect(book)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="font-medium text-sm">{book.title}</div>
              <div className="text-xs text-neutral-600">{book.author}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <span className="text-[16px] font-bold text-error-500">{error}</span>
      )}
    </div>
  );
};

export default BookAutocomplete;
